import { inject, onBeforeUnmount, provide, ref } from "vue";
import type { InjectionKey, Ref } from "vue";
import { useAgentChat } from "@/composables/agent/useAgentChat";
import { useAgentActions } from "@/composables/agent/useAgentActions";
import { useReplyDraftInbox } from "@/composables/agent/useReplyDraftInbox";
import type { AgentAction } from "@/dtos/Agent.dto";

export type AssistantChat = ReturnType<typeof useAgentChat> &
  ReturnType<typeof useAgentActions> & {
    /** Ticks so draft cards notice they expired. */
    now: Ref<number>;
    /** The composer text, kept here so it survives switching between docked and expanded. */
    draft: Ref<string>;
    /** The docked panel shows the conversation list in place of the chat. */
    showHistory: Ref<boolean>;
    /** AI reply drafts the rep hasn't opened yet (the bubble's "3 new replies"). */
    inbox: ReturnType<typeof useReplyDraftInbox>;
    /** Whether the rep can see the assistant now; only then does opening a thread read it. */
    isVisible: Ref<boolean>;
  };

const ASSISTANT_CHAT_KEY: InjectionKey<AssistantChat> = Symbol("assistant-chat");

/**
 * Owns the floating CRM assistant's chat, once for the whole app (AssistantWidget), so a
 * conversation carries on across pages and between the docked panel and the expanded modal.
 */
export function provideAssistantChat(): AssistantChat {
  const chat = useAgentChat({ fixedMode: "assistant" });
  // Set by AssistantWidget while its window is open.
  const isVisible = ref(false);

  // A new draft posted to the open thread, or its draft rewritten elsewhere: re-read it. With
  // the window shut it stays unread, so the bubble still announces it.
  const inbox = useReplyDraftInbox((event) => {
    const openId = chat.conversationId.value;
    if (!openId || event.conversationId !== openId || chat.isSending.value) return;
    chat.open(openId, { reload: true, markRead: isVisible.value });
  });

  const onActionUpdate = (action: AgentAction) => {
    chat.updateAction(action);
    // A sent or dismissed draft isn't news any more.
    if (action.status !== "pending") inbox.refresh();
  };
  const actions = useAgentActions(onActionUpdate);

  const now = ref(Date.now());
  const draft = ref("");
  const showHistory = ref(false);
  const timer = window.setInterval(() => (now.value = Date.now()), 15_000);
  onBeforeUnmount(() => window.clearInterval(timer));

  const value: AssistantChat = {
    ...chat,
    ...actions,
    now,
    draft,
    showHistory,
    inbox,
    isVisible,
  };
  provide(ASSISTANT_CHAT_KEY, value);
  return value;
}

/** The assistant chat provided by AssistantWidget. */
export function useAssistantChat(): AssistantChat {
  const value = inject(ASSISTANT_CHAT_KEY);
  if (!value) throw new Error("useAssistantChat() must be used inside AssistantWidget.");
  return value;
}
